'use client';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { produce } from 'immer';
import cookies from 'js-cookie';

import axios from '../../utils/axios';

import { NotificationDto, NotificationListDto, UnreadNotificationCountDto } from './types';

type NotificationsState = {
  notifications: {
    data: NotificationDto[]
    unreadCount: number
    loading: boolean
    error: boolean
  }
  fetchNotifications: () => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markEventAsRead: (notificationId: string) => Promise<void>
};

export const useNotifications = create(
  devtools<NotificationsState>(
    (set) => ({
      loading: true,
      notifications: {
        data: [],
        unreadCount: 0,
        loading: true,
        error: false,
      },
      async fetchNotifications() {
        await axios
          .get<NotificationListDto>('/customer/notifications')
          .then((response) => {
            set((state) => (
              produce(state, (draft) => {
                draft.notifications.data = response.data.results;
                draft.notifications.loading = false;
              })
            ));
          })
          .catch((error) => {
            console.warn('Failed to fetch notifications!', error);

            set((state) => (
              produce(state, (draft) => {
                draft.notifications.loading = false;
                draft.notifications.error = true;
              })
            ));
          });
      },
      async fetchUnreadCount() {
        const unreadCountResult = await axios
          .get<UnreadNotificationCountDto>('/customer/notifications/unread/count')
          .then(({ data }) => data)
          .catch((error) => {
            console.warn('Failed to fetch unread notification count!', error);

            return {
              count: 0,
            };
          });

        set((state) => (
          produce(state, (draft) => {
            draft.notifications.unreadCount = unreadCountResult.count;
          })
        ));
      },
      async markEventAsRead(notificationId) {
        await axios.patch(
          `/customer/notifications/${notificationId}/read`,
        );

        set((state) => ({
          notifications: {
            ...state.notifications,
            unreadCount: state.notifications.unreadCount - 1,
            data: state.notifications.data.map((notification) => {
              if (notification.id === notificationId) {
                return { ...notification, isRead: true };
              }

              return notification;
            }),
          },
        }));
      },
    }),
  ),
);

type CartState = {
  cart: {
    id?: string
    count?: string
    href?: string
  }
  fetchCart: () => void
};

export const useCart = create<CartState>(
  (set) => ({
    cart: {
      id: undefined,
      count: undefined,
      url: undefined,
    },
    fetchCart() {
      const cartId = cookies.get('cartId');
      const cartCount = cookies.get('cartCount');
      const cartUrl = genCartUrl(cartId);

      set({
        cart: {
          id: cartId,
          count: cartCount,
          href: cartUrl && cartUrl.href,
        },
      });
    },
  }),
);

export function genCartUrl(cartId?: string): URL | undefined {
  if (!cartId) {
    return undefined;
  }

  const cartUrlBase = (
    cookies.get('cartUri') || '/kjop-forsikring/tilbud'
  );

  return new URL(
    `${cartUrlBase}/${cartId}`,
    window.location.href,
  );
}
